/* eslint-disable no-plusplus */

'use client';

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useCollection } from '@react-stately/collections';
import { ListCollection } from '@react-stately/list';
import type { MultipleSelectionStateProps } from '@react-stately/selection';
import {
  SelectionManager,
  useMultipleSelectionState,
} from '@react-stately/selection';
import type {
  Collection,
  CollectionStateBase,
  Key,
  Node,
} from '@react-types/shared';

export type ListProps<T> = {
  /** Filter function to generate a filtered list of nodes. */
  filter?: (nodes: Iterable<Node<T>>) => Iterable<Node<T>>;
  /** @private */
  suppressTextValueWarning?: boolean;
} & CollectionStateBase<T> &
  MultipleSelectionStateProps;

export type ListState<T> = {
  /** A collection of items in the list. */
  collection: Collection<Node<T>>;

  /** A set of items that are disabled. */
  disabledKeys: Set<Key>;

  /** A selection manager to read and update multiple selection state. */
  selectionManager: SelectionManager;
};

/**
 * Provides state management for list-like components. Handles building a collection
 * of items from props, and manages multiple selection state.
 *
 * Code mostly copied from: https://github.com/adobe/react-spectrum/blob/109ea066ed6d953abd2ee2cd7f8051823ae7cf52/packages/%40react-stately/list/src/useListState.ts#L41
 * But we needed to be able to pass in a context value to the collection for
 * purposes of styling.
 */
export function useListState<T extends object>(
  props: ListProps<T>,
  context: any
): ListState<T> {
  const { filter } = props;
  const selectionState = useMultipleSelectionState(props);

  const disabledKeys = useMemo(
    () => (props.disabledKeys ? new Set(props.disabledKeys) : new Set<Key>()),
    [props.disabledKeys]
  );

  const factory = useCallback(
    (nodes: Iterable<Node<T>>) =>
      filter ? new ListCollection(filter(nodes)) : new ListCollection(nodes),
    [filter]
  );

  const collection = useCollection(props, factory, context);

  const selectionManager = useMemo(
    () => new SelectionManager(collection, selectionState),
    [collection, selectionState]
  );

  const cachedCollection = useRef<Collection<Node<T>> | null>(null);

  // copied from https://github.com/adobe/react-spectrum/blob/main/packages/%40react-stately/list/src/useListState.ts
  // when a focused item is deleted, refocus another item
  useEffect(() => {
    if (
      selectionState.focusedKey != null &&
      !collection.getItem(selectionState.focusedKey) &&
      cachedCollection.current
    ) {
      const startItem = cachedCollection.current.getItem(
        selectionState.focusedKey
      );
      const cachedItemNodes = [...cachedCollection.current.getKeys()]
        .map((key) => {
          const itemNode = cachedCollection.current!.getItem(key);
          return itemNode?.type === 'item' ? itemNode : null;
        })
        .filter((node) => node !== null);
      const itemNodes = [...collection.getKeys()]
        .map((key) => {
          const itemNode = collection.getItem(key);
          return itemNode?.type === 'item' ? itemNode : null;
        })
        .filter((node) => node !== null);
      const diff: number =
        (cachedItemNodes?.length ?? 0) - (itemNodes?.length ?? 0);
      let index = Math.min(
        diff > 1
          ? Math.max((startItem?.index ?? 0) - diff + 1, 0)
          : startItem?.index ?? 0,
        (itemNodes?.length ?? 0) - 1
      );
      let newNode: Node<T> | null = null;
      let isReverseSearching = false;
      while (index >= 0) {
        if (!selectionManager.isDisabled(itemNodes[index].key)) {
          newNode = itemNodes[index];
          break;
        }
        // Find next, not disabled item.
        if (index < itemNodes.length - 1 && !isReverseSearching) {
          index++;
          // Otherwise, find previous, not disabled item.
        } else {
          isReverseSearching = true;
          if (index > (startItem?.index ?? 0)) {
            index = startItem?.index ?? 0;
          }
          index--;
        }
      }
      selectionState.setFocusedKey(newNode ? newNode.key : null);
    }
    cachedCollection.current = collection;
  }, [collection, selectionManager, selectionState, selectionState.focusedKey]);

  return {
    collection,
    selectionManager,
    disabledKeys,
  };
}
